import { GoogleError } from "google-gax";

import { FirestoreErrorCode } from "./firestoreTypes";
import {
  STATUS_TO_STRING_MAP,
  FIRESTORE_ERROR_MAPPINGS,
} from "./firestoreMappings";

import type { ErrorContext } from "@/errors";
import { OakGoogleClassroomException, ErrorSeverity } from "@/errors";

/**
 * Type guard to check for GoogleErrors from Firestore
 * GoogleError has a numeric 'code' property from the Status enum
 */
function isGoogleError(error: unknown): error is GoogleError {
  return (
    error instanceof GoogleError &&
    typeof (error as GoogleError).code === "number"
  );
}

/**
 * Error handler for Firestore operations
 * Converts errors for more accurate handling and throws custom OakGoogleClassroomException
 *
 * @param error - The caught error (typically GoogleError from Firestore)
 * @param context - Context about where/why the error occurred
 * @returns Never (always throws OakGoogleClassroomException)
 *
 * @example
 * try {
 *   await firestore.collection('users').doc(id).get();
 * } catch (error) {
 *   handleFirestoreError(error, {
 *     operation: 'getConnectedUserCredentials',
 *     service: 'firestore',
 *     userId: loginHint
 *   });
 * }
 */
export function handleFirestoreError(
  error: unknown,
  context: ErrorContext,
): never {
  console.error(`[${context.service}] Error in ${context.operation}:`, error);

  let errorCode: FirestoreErrorCode | undefined;
  let originalError: Error;

  if (isGoogleError(error)) {
    errorCode =
      error.code !== undefined ? STATUS_TO_STRING_MAP[error.code] : undefined;
    originalError = error;
  } else if (error instanceof Error) {
    originalError = error;
  } else {
    originalError = new Error(String(error));
  }

  const firestoreErrorMapping =
    errorCode && errorCode in FIRESTORE_ERROR_MAPPINGS
      ? FIRESTORE_ERROR_MAPPINGS[errorCode]
      : {
          userMessage: "An unexpected error occurred. Please try again.",
          internalMessage: "Unknown error type",
          shouldRetry: false,
          severity: ErrorSeverity.Error,
        };

  console.error(
    `[Internal] Error from Firestore: ${firestoreErrorMapping.internalMessage}`,
    {
      code: errorCode,
      operation: context.operation,
      metadata: context.metadata,
    },
  );

  throw new OakGoogleClassroomException(
    firestoreErrorMapping.userMessage,
    context.service,
    {
      code: errorCode,
      shouldRetry: firestoreErrorMapping.shouldRetry,
      severity: firestoreErrorMapping.severity,
      context,
      originalError,
    },
  );
}
